import { useAuthStore } from '../store/authStore';

const API_BASE = import.meta.env.PROD 
  ? 'https://totaros-erp-app.valleyintest.workers.dev' 
  : '';

interface RequestOptions extends RequestInit {
  params?: Record<string, string>;
}

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private getHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    const token = useAuthStore.getState().token;
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  private buildUrl(endpoint: string, params?: Record<string, string>): string {
    const url = new URL(`${this.baseUrl}${endpoint}`);
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          url.searchParams.append(key, value);
        }
      });
    }
    return url.toString();
  }

  async request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    const { params, ...fetchOptions } = options;
    const url = this.buildUrl(endpoint, params);

    const response = await fetch(url, {
      ...fetchOptions,
      headers: {
        ...this.getHeaders(),
        ...fetchOptions.headers,
      },
    });

    if (response.status === 401) {
      useAuthStore.getState().logout();
      window.location.href = '/login';
      throw new Error('認証が必要です');
    }

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'リクエストに失敗しました');
    }

    return data as T;
  }

  get<T>(endpoint: string, params?: Record<string, string>): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET', params });
  }

  post<T>(endpoint: string, body?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  put<T>(endpoint: string, body?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  async upload<T>(endpoint: string, formData: FormData): Promise<T> {
    const token = useAuthStore.getState().token;
    const headers: HeadersInit = {};
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'POST',
      headers,
      body: formData,
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'アップロードに失敗しました');
    }

    return data as T;
  }
}

export const api = new ApiClient(API_BASE);

// 型定義
export interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  status: string;
  avatarUrl?: string;
  lastLoginAt?: string;
  createdAt: string;
}

export interface Account {
  id: string;
  code: string;
  name: string;
  nameKana?: string;
  accountType: string;
  category?: string;
  taxType?: string;
  isActive: boolean;
}

export interface Department {
  id: string;
  code: string;
  name: string;
  parentId?: string;
  level: number;
  path: string;
  isActive: boolean;
  children?: Department[];
}

export interface Partner {
  id: string;
  code: string;
  name: string;
  nameKana?: string;
  partnerType: string;
  invoiceNumber?: string;
  address?: string;
  phone?: string;
  email?: string;
  isActive: boolean;
}

export interface JournalEntry {
  id: string;
  journalNumber: string;
  journalDate: string;
  journalType: string;
  description?: string;
  status: string;
  sourceType?: string;
  lines?: JournalLine[];
}

export interface JournalLine {
  id: string;
  lineNumber: number;
  accountId: string;
  accountCode?: string;
  accountName?: string;
  departmentId?: string;
  departmentName?: string;
  partnerId?: string;
  partnerName?: string;
  debitAmount: string;
  creditAmount: string;
  taxType?: string;
  taxAmount: string;
  description?: string;
}

export interface DashboardData {
  type: string;
  alerts?: {
    pendingJournals: number;
    pendingDrafts: number;
    pendingEvidence: number;
  };
  monthlyPL?: {
    revenue: number;
    expense: number;
    profit: number;
  };
  recentJournals?: JournalEntry[];
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
  };
}

